import { useState } from "react";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import CommonStyles from "@components/CommonStyles";
import { IDeliveryAddress } from "@base/modules/customerDeliveryAddress/customerDeliveryAddress.interface";
import customerDeliveryAddressService from "@base/modules/customerDeliveryAddress/customerDeliveryAddressService";
import { IPatientRecord } from "@base/modules/user/profile/profile.interface";
import { getErrorMsg } from "@base/helpers/common";
import ServiceAddressFields, { IServiceAddress } from "./ServiceAddressFields";

interface IProps {
  open: boolean;
  toggle: () => void;
  /** Absent when adding; the address book entry being corrected otherwise. */
  address?: IDeliveryAddress;
  patientRecord?: IPatientRecord;
  /** The address book is empty, so the new address becomes the patient's default one. */
  isFirstAddress?: boolean;
  onSaved: (address: IDeliveryAddress) => void;
}

export default function CustomerAddressForm({ open, toggle, address, patientRecord, isFirstAddress, onSaved }: IProps) {
  //! state
  const { t } = useTranslation();

  // A new address starts from the patient record, which is what the booking is for anyway.
  const [name, setName] = useState<string>(address?.customer_delivery_name ?? patientRecord?.patient_name ?? "");
  const [phone, setPhone] = useState<string>(address?.customer_delivery_phone ?? (patientRecord?.contact_phone || patientRecord?.patient_phone_number || ""));
  const [administrative, setAdministrative] = useState<IServiceAddress>(
    address
      ? {
          provinceCode: address.customer_delivery_province_code,
          provinceName: address.customer_delivery_province_name,
          wardCode: address.customer_delivery_ward_code,
          wardName: address.customer_delivery_ward_name,
          street: address.customer_delivery_address,
        }
      : {
          provinceCode: patientRecord?.administrative_province_code,
          provinceName: patientRecord?.administrative_province_name,
          wardCode: patientRecord?.administrative_ward_code,
          wardName: patientRecord?.administrative_ward_name,
          street: patientRecord?.patient_address,
        },
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);
  // A new address is for the patient record itself, so its name and phone go along unseen. They
  // stay on screen when the record lacks them, since the API refuses an address without them.
  const [showContact] = useState<boolean>(!!address || !name.trim() || !phone.trim());
  const [error, setError] = useState<string>("");

  //! function
  const onConfirm = async () => {
    if (!name.trim() || !phone.trim()) {
      setError(t("packageBooking.requiredAddressContact"));
      return;
    }

    if (!administrative.provinceCode || !administrative.wardCode || !administrative.street?.trim()) {
      setError(t("packageBooking.requiredServiceAddress"));
      return;
    }

    try {
      setIsSaving(true);
      setError("");

      const resp = await customerDeliveryAddressService.save({
        ...address,
        // The server unboxes `primary` without a null check and answers 500 when it is missing.
        primary: address?.primary ?? address?.is_primary ?? !!isFirstAddress,
        customer_delivery_name: name.trim(),
        customer_delivery_phone: phone.trim(),
        customer_delivery_address: administrative.street?.trim(),
        customer_delivery_province_code: administrative.provinceCode,
        customer_delivery_province_name: administrative.provinceName,
        customer_delivery_ward_code: administrative.wardCode,
        customer_delivery_ward_name: administrative.wardName,
      });

      onSaved(resp.data ?? {});
    } catch (saveError) {
      // The server's own reason is what tells a tester (and support) why the address was refused.
      const response = (saveError as AxiosError)?.response;
      const message = response ? String(getErrorMsg(saveError)) : "";
      const reason = message && message !== "Something wrong!" ? message : response ? `HTTP ${response.status}` : "";

      setError(reason ? `${t("packageBooking.saveAddressFailed")} (${reason})` : t("packageBooking.saveAddressFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  //! render
  return (
    <CommonStyles.ModalConfirmationUI
      open={open}
      toggle={toggle}
      type="normal"
      title={address ? t("packageBooking.editAddress") : t("packageBooking.addAddress")}
      okText={t("Submit")}
      okButtonProps={{ loading: isSaving }}
      onConfirm={onConfirm}
      content={
        <div className="flex flex-col gap-3">
          {showContact ? (
            <>
              <CommonStyles.InputUI
                variant="filled"
                required
                label={t("packageBooking.receiverName")}
                value={name}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
              />

              <CommonStyles.InputUI
                variant="filled"
                required
                label={t("policyActivation.phoneNumber")}
                value={phone}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setPhone(event.target.value.trim())}
              />
            </>
          ) : null}

          <ServiceAddressFields label={t("Address")} required labeledFields value={administrative} onChange={setAdministrative} />

          {error ? <span className="text-common-red500 text-xs font-normal font-['Inter']">{error}</span> : null}
        </div>
      }
    />
  );
}
